import { useEffect } from 'react';

interface Args {
  page: number;
  setPage: (updater: number | ((page: number) => number)) => void;
  pageSize: number;
  total: number;
  /** The server response's own offset, when available. Preferred over
   *  `page * pageSize` for display so a `keepPreviousData` refetch can't pair
   *  a new offset with the previous fetch's still-showing rows. */
  responseOffset?: number;
}

interface PageWindow {
  pageCount: number;
  shownOffset: number;
}

/**
 * Derives paging display state from a page position and a server total, and
 * snaps `page` back to the last page if `total` shrinks below it (e.g. a row
 * was deleted while its page was open) — otherwise the view is stranded on an
 * empty page forever.
 *
 * Doesn't own `page` itself: the caller's `useState` supplies it, since the
 * offset it implies (`page * pageSize`) is needed to build the list query,
 * which runs before `total` (the query's own result) exists.
 */
export function usePageWindow({
  page,
  setPage,
  pageSize,
  total,
  responseOffset,
}: Args): PageWindow {
  const offset = page * pageSize;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    if (total > 0 && offset >= total) setPage(pageCount - 1);
  }, [total, offset, pageCount, setPage]);

  return { pageCount, shownOffset: responseOffset ?? offset };
}
